import { useRef } from 'preact/hooks';

import { Text } from '../UNSAFE_Text';

import { useNavigationListItem } from './useNavigationListItem';
import { Flex } from '../UNSAFE_Flex';
import { Badge } from '../UNSAFE_Badge';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { WarningS } from '../UNSAFE_RedwoodIcons/WarningS';
import { ErrorS } from '../UNSAFE_RedwoodIcons/ErrorS';
import { InformationS } from '../UNSAFE_RedwoodIcons/InformationS';
import { SuccessS } from '../UNSAFE_RedwoodIcons/SuccessS';
import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from '../UNSAFE_NavigationList/themes/NavigationListItemStyles.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/NavigationListItemTheme';
import { ComponentChildren } from 'preact';

type Severity = 'warning' | 'info' | 'none' | 'error' | 'confirmation';

export type BaseNavigationListItemProps<K extends string | number> = {
  /**
   * Key of the NavigationListItem.
   */
  itemKey: K;
  /**
   * Label of the NavigationListItem.
   */
  label?: string;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * The content to be rendered inside the Text component as a metadata.
   */
  metadata?: string;
  /**
   * The status icon to be rendered after the label.
   */
  severity?: Severity;
  /**
   * The content to be rendered for remove icon
   */
  removeIcon?: ComponentChildren;
};

/*TODO: JET-58535. Add ripple effect when touching navigationListItem. */
export function BaseNavigationListItem<K extends string | number>({
  itemKey,
  label,
  badge,
  metadata,
  severity = 'none',
  removeIcon
}: BaseNavigationListItemProps<K>) {
  const itemRef = useRef<HTMLLIElement | null>(null);

  //Logic of the component handled here
  const {
    itemId,
    itemHandlers,
    itemClasses,
    isSelected,
    labelContainerClasses,
    labelContainerStyle
  } = useNavigationListItem({
    itemKey
  });

  const severityRequirement = !(severity === 'none' || severity === undefined);

  const IconComponent = (severityValue: Severity) => {
    switch (severityValue) {
      case 'error':
        return <ErrorS color="danger" />;
      case 'warning':
        return <WarningS color="warning" />;
      case 'info':
        return <InformationS color="info" />;
      case 'confirmation':
        return <SuccessS color="success" />;
      default:
        return null;
    }
  };

  const {
    styles: { iconLabelContainer, iconContainer, navigationItemMetadata }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme
  );

  const navigationItemMatadata = classNames([navigationItemMetadata]);

  const endIconContainerClasses = classNames([iconLabelContainer, iconContainer]);
  const {
    styles: { navigationlistRemoveIcon }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme
  );

  const removeClass = classNames([navigationlistRemoveIcon]);

  return (
    <li
      role="option"
      {...itemHandlers}
      data-oj-key={itemKey}
      id={itemId}
      class={itemClasses}
      aria-selected={isSelected}
      ref={itemRef}>
      {label && (
        <span class={labelContainerClasses} style={labelContainerStyle}>
          <Text size="inherit" weight="inherit" variant="inherit">
            {label}
          </Text>
        </span>
      )}
      {(metadata || severityRequirement || badge || removeIcon) && (
        <Flex gap="2x" align="center" justify="end" flex="1 1 auto">
          {' '}
          {badge && (
            <span class={endIconContainerClasses}>
              <Badge size="md" variant="neutralSubtle">
                {badge}
              </Badge>
            </span>
          )}
          {metadata && (
            <span class={navigationItemMatadata}>
              <Text>{'( ' + metadata + ' )'}</Text>
            </span>
          )}
          {severityRequirement && (
            <span class={endIconContainerClasses}>{IconComponent(severity)}</span>
          )}
          {removeIcon && <span class={removeClass}>{removeIcon}</span>}
        </Flex>
      )}
    </li>
  );
}
